<template>
  <v-navigation-drawer
    :mini-variant="mini"
    :expand-on-hover="mini"
    :value="drawer"
    :right="$vuetify.rtl"
    fixed
    app
    floating
    class="my-4 ms-4 border-radius-lg"
    :class="drawerClasses"
    :data-color="sidebarColor"
    :data-theme="sidebarTheme"
    @mouseenter="onSidebarHover"
    @mouseleave="onSidebarLeave"
    @mouseenter.native="onSidebarHoverNative"
    @mouseleave.native="onSidebarLeaveNative"
  >
    <!-- Hover Detection Wrapper -->
    <v-hover v-slot="{ hover }">
      <div
        style="width: 100%; height: 100%"
        @mouseenter="onVHoverEnter(hover)"
        @mouseleave="onVHoverLeave(hover)"
      >
        <v-list-item class="pa-0" @click="$router.push('/locations')">
          <v-list-item-content class="pa-0">
            <v-list-item-title class="title d-flex align-center mb-5">
              <div class="pa-5 d-flex align-center">
                <v-img
                  :src="
                    showWhileExpanded
                      ? logoSrc
                      : require('@/assets/favicon.png')
                  "
                  class="navbar-brand-img"
                  :class="{ 'mb-5': showWhileExpanded, 'ml-0': !mini }"
                  :style="{
                    position: showWhileExpanded ? 'absolute' : 'absolute',
                    left: showWhileExpanded ? '' : '0%',
                    top: showWhileExpanded ? '25%' : '50%',
                  }"
                  :height="showWhileExpanded ? 55 : 55"
                  :width="showWhileExpanded ? 200 : 55"
                />
              </div>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <hr class="horizontal" style="border-color: #fff" />

        <v-expansion-panels
          v-model="activePanel"
          flat
          accordion
          tile
          class="navigation-panels pr-2 pl-2 mt-5"
        >
          <v-expansion-panel>
            <v-expansion-panel-header expand-icon="mdi-menu-down">
              <v-list-item-icon>
                <v-icon class="text-gradient"> mdi-map-marker </v-icon>
                <h5 class="text-left font-weight-light ml-4" v-if="showWhileExpanded">
                  Location Management
                </h5>
              </v-list-item-icon>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-list nav dense>
                <v-list-item
                  :color="nightModeIconColor"
                  to="/locations"
                  :class="{ 'active-item': $route.path === '/locations' }"
                >
                  <v-list-item-content>
                    <h6
                      class="text-left font-weight-light ml-4"
                      :class="nightModeTextClass"
                    >
                      Locations
                    </h6>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  :color="nightModeIconColor"
                  to="/add-location"
                  :class="{ 'active-item': $route.path === '/add-location' }"
                >
                  <v-list-item-content>
                    <h6
                      class="text-left font-weight-light ml-4"
                      :class="nightModeTextClass"
                    >
                      Add Location
                    </h6>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header expand-icon="mdi-menu-down">
              <v-list-item-icon>
                <v-icon class="text-gradient"> mdi-account </v-icon>
                <h5 class="text-left font-weight-light ml-4" v-if="showWhileExpanded">Account</h5>
              </v-list-item-icon>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-list nav dense>
                <v-list-item
                  :color="nightModeIconColor"
                  to="/profile"
                  :class="{ 'active-item': $route.path === '/profile' }"
                >
                  <v-list-item-content>
                    <h6
                      class="text-left font-weight-light ml-4"
                      :class="nightModeTextClass"
                    >
                      Profile
                    </h6>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  :color="nightModeIconColor"
                  to="/company"
                  :class="{ 'active-item': $route.path === '/company' }"
                >
                  <v-list-item-content>
                    <h6
                      class="text-left font-weight-light ml-4"
                      :class="nightModeTextClass"
                    >
                      Company
                    </h6>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header expand-icon="mdi-menu-down">
              <v-list-item-icon>
                <v-icon class="text-gradient"> mdi-chart-box </v-icon>
                <h5 class="text-left font-weight-light ml-4" v-if="showWhileExpanded">Dashboards</h5>
              </v-list-item-icon>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-list nav dense>
                <v-list-item
                  :color="nightModeIconColor"
                  to="/reports"
                  :class="{ 'active-item': $route.path === '/reports' }"
                >
                  <v-list-item-content>
                    <h6
                      class="text-left font-weight-light ml-4"
                      :class="nightModeTextClass"
                    >
                      All Dashboards
                    </h6>
                  </v-list-item-content>
                </v-list-item>

                <!-- Recent Dashboards -->
                <v-list-item
                  v-for="dashboard in recentDashboards"
                  :key="dashboard.dashboardId"
                  :color="nightModeIconColor"
                  :to="`/reports/${dashboard.dashboardId}`"
                  :class="{
                    'active-item':
                      $route.path === `/reports/${dashboard.dashboardId}`,
                  }"
                >
                  <v-list-item-content>
                    <h6
                      class="text-left font-weight-light ml-4 dashboard-name"
                      :class="nightModeTextClass"
                    >
                      {{ dashboard.name }}
                    </h6>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header expand-icon="mdi-menu-down">
              <v-list-item-icon>
                <v-icon class="text-gradient"> mdi-help-circle </v-icon>
                <h5 class="text-left font-weight-light ml-4" v-if="showWhileExpanded">
                  Support Resources
                </h5>
              </v-list-item-icon>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-list nav dense>
                <v-list-item
                  :color="nightModeIconColor"
                  href="https://forms.monday.com/forms/c2d463504a8415ce4a6fc7c5da884768?r=use1"
                  target="_blank"
                >
                  <v-list-item-content>
                    <h6
                      class="text-left font-weight-light ml-4"
                      :class="nightModeTextClass"
                    >
                      Request A Feature
                    </h6>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  :color="nightModeIconColor"
                  href="https://forms.monday.com/forms/b7b068c773206ee96435d84bf0478326?r=use1"
                  target="_blank"
                >
                  <v-list-item-content>
                    <h6
                      class="text-left font-weight-light ml-4"
                      :class="nightModeTextClass"
                    >
                      Report A Bug
                    </h6>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <hr class="horizontal mb-4" style="border-color: #fff" />

        <!-- Night Mode Toggle -->
        <div class="px-4" style="bottom: 75px; position: absolute; width: 100%">
          <v-row
            align="center"
            justify="center"
            no-gutters
            v-if="showWhileExpanded"
          >
            <v-col cols="12" class="text-center">
              <span class="text-sm mb-2 d-block" :class="nightModeTextClass">
                Theme
              </span>
              <v-btn-toggle
                :value="$store.getters.isNightMode ? 1 : 0"
                @change="handleThemeChange"
                mandatory
                class="theme-toggle"
                tile
              >
                <v-btn
                  small
                  tile
                  :color="!$store.getters.isNightMode ? 'grey lighten-3' : ''"
                  :outlined="$store.getters.isNightMode"
                >
                  <v-icon>mdi-weather-sunny</v-icon>
                  Light Mode
                </v-btn>
                <v-btn
                  small
                  tile
                  :color="$store.getters.isNightMode ? 'primary' : ''"
                  :outlined="!$store.getters.isNightMode"
                >
                  <v-icon>mdi-weather-night</v-icon>
                  Dark Mode
                </v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>

          <!-- Mini Mode Theme Toggle - Icons Only -->
          <div v-else class="mini-theme-toggle">
            <v-btn
              icon
              small
              @click="handleThemeChange"
              :color="nightModeIconColor"
              class="mb-2"
            >
              <v-icon>{{
                $store.getters.isNightMode
                  ? "mdi-weather-night"
                  : "mdi-weather-sunny"
              }}</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Sign Out Button -->
        <v-row
          align="center"
          justify="center"
          no-gutters
          v-if="showWhileExpanded"
        >
          <v-btn
            icon
            style="bottom: 25px; position: absolute"
            @click="logout"
            :width="showWhileExpanded ? '48px' : '100%'"
            left
          >
            <v-icon class="mr-2" v-if="isHovered || !mini">mdi-logout</v-icon>
            <v-icon v-else>mdi-logout</v-icon>
            <span v-if="isHovered || !mini">Sign Out</span>
          </v-btn>
        </v-row>
        <v-row align="center" justify="center" no-gutters v-else>
          <v-btn
            icon
            style="bottom: 25px; position: absolute"
            @click="logout"
            :width="'48px'"
            left
          >
            <v-icon>mdi-logout</v-icon>
          </v-btn>
        </v-row>

        <!-- Show different content based on hover state -->
      </div>
    </v-hover>
  </v-navigation-drawer>
</template>

<script>
export default {
  name: "drawer",
  props: {
    drawer: {
      type: Boolean,
      default: null,
    },
    sidebarColor: {
      type: String,
      default: "white",
    },
    sidebarTheme: {
      type: String,
      default: "transparent",
    },
    mini: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    activePanel: null,
    isHovered: false,
  }),
  computed: {
    showWhileExpanded() {
      return (this.mini && this.isHovered) || !this.mini;
    },
    isAccountActive() {
      return ["/locations", "/add-location"].includes(this.$route.path);
    },
    isLocationActive() {
      return ["/profile", "/company"].includes(this.$route.path);
    },
    isReportsActive() {
      return (
        ["/reports"].includes(this.$route.path) ||
        this.$route.path.startsWith("/reports/")
      );
    },
    isSupportActive() {
      return false; // External links are never "active"
    },
    recentDashboards() {
      // Get up to 5 most recent dashboards from the store
      const dashboards = this.$store.getters.userDashboards || [];
      return dashboards.slice(0, 5);
    },
    logoSrc() {
      return this.$store.getters.isNightMode
        ? require("@/assets/integrity.svg")
        : require("@/assets/integrity-black.svg");
    },
    drawerClasses() {
      const classes = [];

      if (this.$vuetify.breakpoint.mobile) {
        classes.push(this.$store.getters.isNightMode ? "bg-dark" : "bg-white");
      }

      if (this.$store.getters.isNightMode) {
        classes.push("night-mode-drawer");
      }

      return classes.join(" ");
    },
    nightModeTextClass() {
      return this.$store.getters.isNightMode ? "text-white" : "text-dark";
    },
    nightModeIconColor() {
      return this.$store.getters.isNightMode ? "white" : "grey darken-2";
    },
    sidebarState() {
      if (this.mini && this.isHovered) return "mini-hovered";
      if (this.mini) return "mini";
      return "full";
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(newRoute) {
        // Automatically expand the panel containing the active route
        if (this.isAccountActive) {
          this.activePanel = 0;
        } else if (this.isLocationActive) {
          this.activePanel = 1;
        } else if (this.isReportsActive) {
          this.activePanel = 2;
        } else if (this.isSupportActive) {
          this.activePanel = 3;
        }
      },
    },
    // Watch for changes in userDashboards to ensure sidebar stays updated
    "$store.getters.userDashboards": {
      handler(newDashboards) {
        // Dashboard list updated
      },
      deep: true,
    },
    // Close expansion panels when switching to mini mode
    mini: {
      handler(newMini) {
        if (newMini) {
          this.activePanel = null; // Close all expansion panels
        }
      },
      immediate: true,
    },
    isHovered(newVal) {
      if (newVal && this.mini) {
        // Mini sidebar expanded on hover
      }
    },
  },
  methods: {
    logout() {
      this.$store.dispatch("logoutUser");
    },
    handleThemeChange(value) {
      this.$store.dispatch("toggleNightMode");
    },
    async loadDashboards() {
      try {
        await this.$store.dispatch("loadUserDashboards");
      } catch (error) {
        console.error("Error loading dashboards in sidebar:", error);
      }
    },
    onSidebarHover() {
      this.isHovered = true;
      this.$emit('hover-change', true);
    },
    onSidebarLeave() {
      this.isHovered = false;
      this.$emit('hover-change', false);
    },
    onSidebarHoverNative() {
      this.isHovered = true;
      this.$emit('hover-change', true);
    },
    onSidebarLeaveNative() {
      this.isHovered = false;
      this.$emit('hover-change', false);
    },
    onWrapperHover() {
      this.isHovered = true;
      this.$emit('hover-change', true);
    },
    onWrapperLeave() {
      this.isHovered = false;
      this.$emit('hover-change', false);
    },
    onVHoverEnter(hover) {
      this.isHovered = true;
      this.$emit('hover-change', true);
    },
    onVHoverLeave(hover) {
      this.isHovered = false;
      this.$emit('hover-change', false);
    },
  },
  async mounted() {
    await this.loadDashboards();
  },
};
</script>

<style scoped>
h5 {
  font-size: 0.85rem !important;
}
h6 {
  font-size: 0.8rem !important;
}
.navigation-panels {
  background: transparent !important;
}

.text-left {
  text-align: left !important;
}

/* Night mode styles */
.bg-dark {
  background-color: #1e1e1e !important;
}

.text-white {
  color: white !important;
}

.text-dark {
  color: #424242 !important;
}

.text-sm {
  font-size: 0.875rem;
}

/* Custom styles for expansion panels */
::v-deep .v-expansion-panel {
  background: transparent !important;
}

::v-deep .v-expansion-panel-header {
  padding: 8px 16px;
}

::v-deep .v-expansion-panel-content__wrap {
  padding: 0;
}

::v-deep .v-list-item {
  padding-left: 32px;
}

/* Hover effects */
::v-deep .v-expansion-panel-header:hover {
  background: rgba(0, 0, 0, 0.04);
}

::v-deep .v-list-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.active-item {
  background-color: rgba(0, 0, 0, 0.08) !important;
  color: #424242 !important;
  font-weight: 600 !important;
}

.active-text {
  color: #424242 !important;
  font-weight: 600 !important;
}

/* Update existing styles */
::v-deep .v-expansion-panel {
  background: transparent !important;
}

::v-deep .v-expansion-panel-header {
  padding: 8px 16px;
}

::v-deep .v-expansion-panel-content__wrap {
  padding: 0;
}

::v-deep .v-list-item {
  padding-left: 32px;
  transition: all 0.3s ease;
}

/* Active state styles */
::v-deep .active-item h6 {
  color: #424242 !important;
  font-weight: 600 !important;
}

.text-gradient {
  background: white;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Active state styles */
::v-deep .active-item h6 {
  color: #424242 !important;
  font-weight: 600 !important;
}

/* Night mode specific styles */
.night-mode-drawer {
  background-color: #1e1e1e !important;
}

::v-deep .night-mode-drawer {
  background-color: #1e1e1e !important;
}

::v-deep .night-mode-drawer .v-expansion-panel-header:hover {
  background: rgba(255, 255, 255, 0.08);
}

::v-deep .night-mode-drawer .v-list-item:hover {
  background: rgba(255, 255, 255, 0.08);
}

::v-deep .night-mode-drawer .active-item {
  background-color: rgba(255, 255, 255, 0.12) !important;
}

::v-deep .night-mode-drawer h5,
::v-deep .night-mode-drawer h6 {
  color: white !important;
}

::v-deep .night-mode-drawer .v-icon {
  color: white !important;
}

::v-deep .night-mode-drawer .horizontal {
  border-color: rgba(255, 255, 255, 0.2) !important;
}

::v-deep .night-mode-drawer .v-btn {
  color: white !important;
}

::v-deep .v-navigation-drawer.bg-dark {
  background-color: #1e1e1e !important;
}

::v-deep .v-navigation-drawer.bg-dark .v-expansion-panel-header:hover {
  background: rgba(255, 255, 255, 0.08);
}

::v-deep .v-navigation-drawer.bg-dark .v-list-item:hover {
  background: rgba(255, 255, 255, 0.08);
}

::v-deep .v-navigation-drawer.bg-dark .active-item {
  background-color: rgba(255, 255, 255, 0.12) !important;
}

::v-deep .v-navigation-drawer.bg-dark h5,
::v-deep .v-navigation-drawer.bg-dark h6 {
  color: white !important;
}

::v-deep .v-navigation-drawer.bg-dark .v-icon {
  color: white !important;
}

::v-deep .v-navigation-drawer.bg-dark .horizontal {
  border-color: rgba(255, 255, 255, 0.2) !important;
}

/* Dashboard name styling */
.dashboard-name {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Mini navigation styling */
.mini-nav {
  padding: 8px 0;
}

.mini-nav .v-list-item {
  min-height: 48px;
  padding: 0 16px;
  margin: 4px 0;
  justify-content: center;
}

.mini-nav .v-list-item-icon {
  margin: 0;
  justify-content: center;
}

/* Mini mode favicon styling */
.navbar-brand-img-mini {
  margin: 0 auto;
}

/* Mini theme toggle styling */
.mini-theme-toggle {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Drawer width adjustments for mini mode */
::v-deep .v-navigation-drawer--mini-variant {
  width: 80px !important;
}

::v-deep
  .v-navigation-drawer--mini-variant:not(.v-navigation-drawer--is-mouseover) {
  width: 80px !important;
}

/* Theme toggle styling */
::v-deep .theme-toggle {
  display: flex;
  justify-content: center;
}

::v-deep .theme-toggle .v-btn {
  min-width: 48px !important;
  height: 40px !important;
  margin: 0 0;
}

::v-deep .theme-toggle .v-btn .v-icon {
  font-size: 20px !important;
}

::v-deep .theme-toggle .v-btn--outlined {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}

::v-deep .night-mode-drawer .theme-toggle .v-btn--outlined {
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

::v-deep .v-navigation-drawer.bg-dark .theme-toggle .v-btn--outlined {
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}
</style>
